import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import FilterBar from '../components/FilterBar';
import GarmentGrid from '../components/GarmentGrid';
import GarmentModal from '../components/GarmentModal';
import Footer from '../components/Footer';
import { Plus } from 'lucide-react';
import { isAdminMode } from '../utils/adminMode';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CatalogPage = () => {
  const navigate = useNavigate();
  const [garments, setGarments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    gender: 'all',
    category: 'all',
    search: ''
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGarment, setSelectedGarment] = useState(null);
  const { toast } = useToast();

  // Fetch garments
  const fetchGarments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/garments`);
      setGarments(response.data);
    } catch (error) {
      console.error('Error fetching garments:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las prendas',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Initialize database with Collection Dreams
  const initializeDatabase = async () => {
    try {
      await axios.post(`${API}/garments/init/collection-dreams`);
      toast({
        title: 'Éxito',
        description: 'Collection Dreams inicializada'
      });
      fetchGarments();
    } catch (error) {
      console.error('Error initializing:', error);
    }
  };

  useEffect(() => {
    fetchGarments();
  }, []);

  // Filter garments
  const filteredGarments = useMemo(() => {
    return garments.filter(garment => {
      const matchesGender = !filters.gender || filters.gender === 'all' || garment.gender === filters.gender;
      const matchesCategory = !filters.category || filters.category === 'all' || garment.category === filters.category;
      const matchesSearch = !filters.search || 
        garment.name.toLowerCase().includes(filters.search.toLowerCase());
      
      return matchesGender && matchesCategory && matchesSearch;
    });
  }, [garments, filters]);

  // Get unique categories
  const categories = useMemo(() => {
    return [...new Set(garments.map(g => g.category))];
  }, [garments]);

  const handleCreate = () => {
    setSelectedGarment(null);
    setIsModalOpen(true);
  };

  const handleEdit = (garment) => {
    setSelectedGarment(garment);
    setIsModalOpen(true);
  };

  const handleDelete = async (garmentId) => {
    try {
      await axios.delete(`${API}/garments/${garmentId}`);
      toast({
        title: 'Éxito',
        description: 'Prenda eliminada correctamente'
      });
      fetchGarments();
    } catch (error) {
      console.error('Error deleting garment:', error);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la prenda',
        variant: 'destructive'
      });
    }
  };

  const handleSave = () => {
    setIsModalOpen(false);
    fetchGarments();
  };

  return (
    <div className="min-h-screen bg-white text-black">
      <Header onCartClick={() => navigate('/cart')} />
      
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Editorial Header */}
        <div className="mb-8 sm:mb-12">
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-neutral-500 mb-3">
            Catálogo completo
          </p>
          <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black uppercase tracking-tighter leading-none">
            Shop All
          </h1>
        </div>

        {/* Add New Button (Admin only) */}
        {isAdminMode() && (
          <div className="flex justify-end mb-4">
            <button
              onClick={handleCreate}
              className="bg-black text-white text-xs font-bold uppercase tracking-[0.2em] px-6 py-3 hover:bg-neutral-800 transition-colors flex items-center gap-2"
              data-testid="add-garment-button"
            >
              <Plus className="w-4 h-4" strokeWidth={2} />
              Añadir prenda
            </button>
          </div>
        )}

        {/* Filters */}
        <FilterBar
          filters={filters}
          setFilters={setFilters}
          categories={categories}
        />

        {/* Results Count */}
        <div className="mb-8">
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-neutral-500" data-testid="results-count">
            {filteredGarments.length} {filteredGarments.length === 1 ? 'prenda' : 'prendas'}
          </p>
        </div>

        {/* Garments Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-24">
            <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <GarmentGrid
            garments={filteredGarments}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        )}
      </main>

      {/* Modal */}
      <GarmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        garment={selectedGarment}
        categories={categories}
      />

      <Toaster />
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CatalogPage;